<template>
	<div class="wbp" :data-wbp-ready="loading ? 'false' : 'true'">
		<main class="wbp-wrap">
			<nav class="wbp-crumb" aria-label="Breadcrumb">
				<router-link to="/">Home</router-link>
				<span aria-hidden="true">/</span>
				<router-link to="/categories/water-birth">Water Birth</router-link>
				<span aria-hidden="true">/</span>
				<span>Build Your Own Water Birth Package</span>
			</nav>

			<section class="wbp-hero">
				<img src="/images/water-birth-package-hero.jpg" alt="Mother holding her newborn in a birth pool surrounded by roses" />
				<div class="wbp-hero-shade"></div>
				<div class="wbp-hero-copy">
					<div class="wbp-kicker">Water Birth</div>
					<h1>Build Your Own Water Birth Package</h1>
					<p>Pick the pool that fits your space and your birth, add the liner made for it, then everything else you need, all on one page. Every item lands in your bag as its own product.</p>
					<div class="wbp-hero-steps">
						<a href="#step-pool"><span>1</span>Choose a pool</a>
						<a href="#step-liner"><span>2</span>Add the right liner</a>
						<a href="#step-acc"><span>3</span>Add accessories</a>
					</div>
				</div>
			</section>

			<section id="step-pool" class="wbp-step wbp-step-wide">
				<div class="wbp-step-head">
					<span class="wbp-num">1</span>
					<div>
						<h2>Choose your birth pool</h2>
						<p>Sizes are outside dimensions. Water is gallons at the manufacturer’s recommended 80% fill. Pick a size and color on any card, then choose it.</p>
					</div>
				</div>

				<div class="wbp-tools">
					<div class="wbp-chips" role="group" aria-label="Filter pools">
						<button v-for="filter in view.filters" :key="filter.id" type="button" class="wbp-chip" :aria-pressed="filter.pressed ? 'true' : 'false'" @click="onFilter(filter.id)">
							{{ filter.label }}
						</button>
					</div>
					<div class="wbp-sort">
						<span>Sort</span>
						<button v-for="sort in view.sorts" :key="sort.id" type="button" class="wbp-pill" :aria-pressed="sort.pressed ? 'true' : 'false'" @click="onSort(sort.id)">
							{{ sort.label }}
						</button>
					</div>
				</div>

				<p v-if="!view.pools.length" class="wbp-empty-filter">No pools match this filter.</p>

				<div class="wbp-cards">
					<article v-for="pool in view.pools" :key="pool.id" class="wbp-card" :class="{ chosen: pool.chosen, oos: pool.outOfStock }">
						<div class="wbp-card-photo">
							<img :src="pool.image" :alt="pool.name" />
							<div class="wbp-badges">
								<span v-for="badge in pool.badges" :key="badge.label" class="wbp-badge" :data-tone="badge.tone">{{ badge.label }}</span>
							</div>
						</div>
						<div class="wbp-card-body">
							<div>
								<div class="wbp-brand">{{ pool.brand }}</div>
								<h3>{{ pool.name }}</h3>
							</div>
							<div class="wbp-price-row">
								<span class="wbp-price">{{ pool.priceLabel }}</span>
								<span v-if="pool.variantLabel" class="wbp-muted">{{ pool.variantLabel }}</span>
							</div>
							<div class="wbp-specs">
								<div><span>Outside</span><strong>{{ pool.ext }}</strong></div>
								<div><span>Water</span><strong>{{ pool.gal }}</strong></div>
								<div><span>Vinyl</span><strong>{{ pool.mil }}</strong></div>
							</div>
							<div v-if="pool.hasSizes" class="wbp-opt-row">
								<span>Size</span>
								<button v-for="size in pool.sizeOpts" :key="size.label" type="button" class="wbp-pill" :aria-pressed="size.pressed ? 'true' : 'false'" @click="onSel(pool.id, 'size', size.label)">
									{{ size.label }}
								</button>
							</div>
							<div v-if="pool.hasColors" class="wbp-opt-row">
								<span>Color</span>
								<button
									v-for="color in pool.colorOpts"
									:key="color.id"
									type="button"
									class="wbp-swatch"
									:class="{ on: color.pressed }"
									:style="{ background: color.hex }"
									:aria-label="color.label"
									:aria-pressed="color.pressed ? 'true' : 'false'"
									:title="color.label"
									@click="onSel(pool.id, 'color', color.id)"
								></button>
								<span>{{ pool.colorName }}</span>
							</div>
							<ul>
								<li v-for="feature in pool.features" :key="feature">{{ feature }}</li>
							</ul>
							<div class="wbp-card-action">
								<button type="button" class="wbp-choose" :class="{ chosen: pool.chosen }" :disabled="pool.outOfStock" @click="onChoose(pool.id)">
									{{ pool.btnLabel }}
								</button>
								<span>{{ pool.linerNote }}</span>
							</div>
						</div>
					</article>
				</div>

				<div class="wbp-compare">
					<button type="button" class="wbp-compare-toggle" :aria-expanded="view.compareOpen ? 'true' : 'false'" @click="onCompare">
						<span>Compare every pool side by side</span>
						<span>{{ view.compareOpen ? 'Hide table' : 'Show table' }}</span>
					</button>
					<div v-if="view.compareOpen" class="wbp-table-wrap">
						<p class="wbp-scroll-hint">Scroll sideways to see every column.</p>
						<table>
							<thead>
								<tr>
									<th>Pool</th>
									<th>Price</th>
									<th>Outside (in.)</th>
									<th>Inside (in.)</th>
									<th>Water</th>
									<th>Vinyl</th>
									<th>Seat</th>
									<th>Liner</th>
									<th><span class="wbp-sr">Select</span></th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="row in view.compareRows" :key="row.id" :class="{ chosen: row.chosen, zebra: row.zebra }">
									<td>
										<div class="wbp-row-name">{{ row.name }}</div>
										<div class="wbp-muted">{{ row.brand }} · {{ row.use }}</div>
									</td>
									<td class="wbp-nowrap">{{ row.price }}</td>
									<td>{{ row.ext }}</td>
									<td>{{ row.int }}</td>
									<td class="wbp-nowrap">{{ row.gal }}</td>
									<td>{{ row.mil }}</td>
									<td>{{ row.seat }}</td>
									<td>{{ row.liner }}</td>
									<td>
										<button type="button" class="wbp-select" :class="{ chosen: row.chosen }" :disabled="row.outOfStock" @click="onChoose(row.id)">
											{{ row.btnLabel }}
										</button>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</section>

			<div class="wbp-layout">
				<div class="wbp-main">
					<section id="step-liner" class="wbp-step">
						<div class="wbp-step-head">
							<span class="wbp-num">2</span>
							<div>
								<h2>Add the right liner</h2>
								<p>A fresh disposable liner keeps the pool clean and makes cleanup fast. We only show liners made for the pool you chose.</p>
							</div>
						</div>
						<div v-if="view.noPool" class="wbp-dashed">Choose a pool in step 1 to see the liners that fit it.</div>
						<div v-else class="wbp-liner">
							<div v-if="view.linerIncluded" class="wbp-included">
								<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7"></path></svg>
								<span>Your {{ view.poolShort }} already comes with one form-fitted custom liner in the box.</span>
							</div>
							<div class="wbp-liner-grid" role="radiogroup" aria-label="Liner">
								<button
									v-for="option in view.linerOpts"
									:key="option.key"
									type="button"
									role="radio"
									class="wbp-liner-opt"
									:class="{ on: option.checked }"
									:aria-checked="option.checked ? 'true' : 'false'"
									:disabled="option.disabled"
									@click="onLiner(option.key)"
								>
									<span class="wbp-liner-title">
										<span>{{ option.title }}</span>
										<span class="wbp-dot" :class="{ on: option.checked }"></span>
									</span>
									<span class="wbp-liner-desc">{{ option.desc }}</span>
									<span class="wbp-liner-meta">
										<span>{{ option.sku }}</span>
										<strong>{{ option.price }}</strong>
									</span>
								</button>
							</div>
							<div v-if="view.showLinerQty" class="wbp-qty-line">
								<span>How many liners?</span>
								<div class="wbp-stepper">
									<button type="button" aria-label="One fewer liner" @click="onLinerQty(-1)">−</button>
									<span>{{ view.linerQty }}</span>
									<button type="button" aria-label="One more liner" @click="onLinerQty(1)">+</button>
								</div>
								<span class="wbp-muted">Most families need one. Midwives often keep a spare.</span>
							</div>
						</div>
					</section>

					<section id="step-acc" class="wbp-step">
						<div class="wbp-step-head">
							<span class="wbp-num">3</span>
							<div>
								<h2>Add water birth accessories</h2>
								<p>Add as many as you like. Use + and − to set quantities.</p>
							</div>
						</div>
						<div v-for="group in view.accGroups" :key="group.title" class="wbp-acc-group">
							<h3>{{ group.title }}</h3>
							<div class="wbp-acc-grid">
								<div v-for="item in group.items" :key="item.id" class="wbp-acc" :class="{ on: item.qty > 0, oos: item.outOfStock }">
									<img :src="item.image" :alt="item.name" />
									<div>
										<div class="wbp-acc-name">{{ item.name }}</div>
										<div class="wbp-acc-desc">{{ item.desc }}</div>
										<div v-if="item.hasOpts" class="wbp-opt-row">
											<button v-for="option in item.opts" :key="option.key" type="button" class="wbp-pill sm" :aria-pressed="option.pressed ? 'true' : 'false'" @click="onAccOption(item.id, option.key)">
												{{ option.label }}
											</button>
										</div>
										<div class="wbp-acc-buy">
											<span>{{ item.outOfStock ? 'Out of stock' : item.priceLabel }}</span>
											<div class="wbp-stepper sm">
												<button type="button" :aria-label="`Remove one ${item.name}`" @click="onAcc(item.id, -1)">−</button>
												<span>{{ item.qty }}</span>
												<button type="button" :aria-label="`Add one ${item.name}`" :disabled="item.outOfStock" @click="onAcc(item.id, 1)">+</button>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</section>
				</div>

				<aside class="wbp-summary" aria-label="Your package">
					<div class="wbp-summary-head">
						<div class="wbp-kicker">Your package</div>
						<div class="wbp-summary-title">{{ view.pkgTitle }}</div>
					</div>
					<div class="wbp-summary-body">
						<p v-if="view.empty" class="wbp-muted">Nothing here yet. Start by choosing a pool, then add a liner and any accessories.</p>
						<div v-for="(line, index) in view.lines" :key="line.kind + line.sku + index" class="wbp-line">
							<div>
								<span class="wbp-line-kind">{{ line.kind }}</span>
								<span class="wbp-line-name">{{ line.name }}</span>
								<span class="wbp-muted">SKU {{ line.sku }} · Qty {{ line.qty }}</span>
							</div>
							<div class="wbp-line-side">
								<strong>{{ line.total }}</strong>
								<button type="button" @click="onRemove(line)">Remove</button>
							</div>
						</div>
					</div>
					<div class="wbp-summary-foot">
						<div class="wbp-subtotal">
							<span>Subtotal <span class="wbp-muted">({{ view.itemCount }} items, {{ view.skuCount }} SKUs)</span></span>
							<strong>{{ view.subtotal }}</strong>
						</div>
						<div v-if="view.warnNoLiner" class="wbp-warn">This pool doesn’t include a liner. We recommend adding one in step 2.</div>
						<p v-if="blockReason" class="wbp-warn">{{ blockReason }}</p>
						<button type="button" class="wbp-add" :disabled="!canAdd" @click="addAll">{{ addLabel }}</button>
						<div v-if="addedMessage" class="wbp-confirm" role="status">
							<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7"></path></svg>
							<span>{{ addedMessage }} <router-link to="/cart">View bag</router-link></span>
						</div>
						<p v-if="addError" class="wbp-warn" role="alert">{{ addError }}</p>
						<p class="wbp-fine">Each item goes into your bag as its own product, so you can still change quantities or remove anything at checkout. Some pools ship directly from the manufacturer.</p>
					</div>
				</aside>
			</div>
		</main>
	</div>
</template>

<script>
import Mango from '../../helpers/mango'
import { COLORS, REFERENCE } from '../../helpers/waterBirthPackage/reference.js'
import { mergeCatalog } from '../../helpers/waterBirthPackage/merge.js'
import { catalogQuery, loadWaterBirthCatalog } from '../../helpers/waterBirthPackage/load.js'
import { addPackageToCart, packageBlockReason } from '../../helpers/waterBirthPackage/addToCart.js'
import {
	initialState,
	viewModel,
	setFilter,
	setSort,
	toggleCompare,
	setSel,
	choosePool,
	setLiner,
	bumpLiner,
	setAccOption,
	bumpAcc,
	removeLine,
} from '../../helpers/waterBirthPackage/builder.js'

export default {
	inject: ['store'],
	data() {
		const catalog = mergeCatalog(REFERENCE, [], { loaded: false })
		return {
			catalog,
			state: initialState(catalog),
			loading: true,
			addedPayload: null,
			addError: '',
		}
	},
	computed: {
		view() {
			return viewModel(this.catalog, this.state, COLORS)
		},
		cartReady() {
			return !!(this.store?.cartInitialized && typeof this.store?.saveCart === 'function' && this.store?.cart)
		},
		blockReason() {
			return packageBlockReason(this.view, {
				loading: this.loading,
				cartReady: this.cartReady,
				catalogLoaded: !!this.catalog?.loaded,
			})
		},
		canAdd() {
			return !!this.view && !this.view.empty && !this.blockReason
		},
		addLabel() {
			if (!this.view || this.view.empty) return 'Add items to build your package'
			return `Add all ${this.view.itemCount} items to Bag · ${this.view.subtotal}`
		},
		addedMessage() {
			if (!this.addedPayload?.length) return ''
			const items = this.addedPayload.reduce((sum, line) => sum + line.quantity, 0)
			return `${this.addedPayload.length} products (${items} items) added to your bag.`
		},
	},
	mounted() {
		this.loadCatalog()
	},
	methods: {
		async loadCatalog() {
			this.loading = true
			try {
				this.catalog = await loadWaterBirthCatalog((ids) => Mango.products(catalogQuery(ids)))
			} finally {
				this.loading = false
			}
		},
		change(next) {
			this.state = next
			this.addedPayload = null
			this.addError = ''
		},
		onFilter(id) {
			this.change(setFilter(this.state, id))
		},
		onSort(id) {
			this.change(setSort(this.state, id))
		},
		onCompare() {
			this.state = toggleCompare(this.state)
		},
		onSel(poolId, key, value) {
			this.change(setSel(this.state, poolId, key, value))
		},
		onChoose(poolId) {
			this.change(choosePool(this.catalog, this.state, poolId))
		},
		onLiner(key) {
			this.change(setLiner(this.state, key))
		},
		onLinerQty(delta) {
			this.change(bumpLiner(this.catalog, this.state, delta))
		},
		onAccOption(itemId, optionKey) {
			this.change(setAccOption(this.state, itemId, optionKey))
		},
		onAcc(itemId, delta) {
			this.change(bumpAcc(this.catalog, this.state, itemId, delta))
		},
		onRemove(line) {
			this.change(removeLine(this.state, line))
		},
		addAll() {
			if (!this.canAdd) return
			const result = addPackageToCart(this.store.cart, this.view.lines)
			if (!result.ok) {
				this.addError = result.name ? `${result.name} could not be added.` : 'Those items could not be added to your bag.'
				this.addedPayload = null
				return
			}
			this.addedPayload = result.added
			this.addError = ''
		},
	},
}
</script>

<style scoped>
.wbp {
	color: theme('colors.ihh-midnight-gray');
	background: theme('colors.ihh-white');
	padding: 88px 0 72px;
}
.wbp-wrap {
	max-width: theme('maxWidth.8xl');
	margin: 0 auto;
	padding: 0 24px;
}
.wbp-crumb {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	font-size: 13px;
	color: theme('colors.ihh-slate-gray');
	padding: 6px 0 16px;
}
.wbp-crumb a {
	color: theme('colors.ihh-slate-gray');
	text-decoration: none;
}
.wbp-crumb a:hover {
	color: theme('colors.ihh-midnight-gray');
	text-decoration: underline;
	text-decoration-color: theme('colors.ihh-dark-salmon');
}
.wbp-hero {
	position: relative;
	border-radius: 10px;
	overflow: hidden;
	min-height: 400px;
	display: flex;
	align-items: flex-end;
	background: theme('colors.ihh-midnight-gray');
}
.wbp-hero img {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
	object-position: center 40%;
}
.wbp-hero-shade {
	position: absolute;
	inset: 0;
	background: theme('colors.ihh-midnight-gray');
	opacity: 0.42;
}
.wbp-hero-copy {
	position: relative;
	padding: 40px 40px 36px;
	max-width: 640px;
	color: theme('colors.ihh-white');
	display: flex;
	flex-direction: column;
	gap: 14px;
}
.wbp-kicker {
	font-size: 13px;
	font-weight: 600;
	letter-spacing: 0.12em;
	text-transform: uppercase;
}
.wbp-hero h1 {
	margin: 0;
	font-size: 46px;
	line-height: 1.05;
	font-weight: 700;
}
.wbp-hero p {
	margin: 0;
	font-size: 17px;
	line-height: 1.5;
	font-weight: 400;
}
.wbp-hero-steps {
	display: flex;
	gap: 10px;
	flex-wrap: wrap;
	padding-top: 6px;
}
.wbp-hero-steps a {
	display: flex;
	align-items: center;
	gap: 8px;
	background: theme('colors.ihh-white');
	color: theme('colors.ihh-midnight-gray');
	border-radius: 999px;
	padding: 9px 16px;
	font-size: 14px;
	font-weight: 500;
	text-decoration: none;
}
.wbp-hero-steps a span,
.wbp-num {
	width: 22px;
	height: 22px;
	border-radius: 999px;
	background: theme('colors.ihh-dark-salmon');
	color: theme('colors.ihh-midnight-gray');
	font-size: 12px;
	font-weight: 600;
	display: flex;
	align-items: center;
	justify-content: center;
	flex: 0 0 auto;
}
.wbp-num {
	width: 36px;
	height: 36px;
	font-size: 16px;
}
.wbp-step-wide {
	padding-top: 40px;
}
.wbp-layout {
	display: flex;
	flex-wrap: wrap;
	gap: 32px;
	align-items: flex-start;
	padding-top: 56px;
}
.wbp-main {
	flex: 999 1 620px;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 56px;
}
.wbp-step {
	display: flex;
	flex-direction: column;
	gap: 20px;
}
.wbp-step-head {
	display: flex;
	align-items: flex-start;
	gap: 14px;
}
.wbp-step h2 {
	margin: 0;
	font-size: 28px;
	font-weight: 600;
}
.wbp-step-head p,
.wbp-muted {
	margin: 0;
	color: theme('colors.ihh-slate-gray');
	font-size: 15px;
	line-height: 1.5;
}
.wbp-muted {
	font-size: 13px;
	color: theme('colors.ihh-slate-gray');
}
.wbp-tools {
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
	align-items: center;
	justify-content: space-between;
}
.wbp-chips,
.wbp-sort,
.wbp-opt-row,
.wbp-hero-steps,
.wbp-qty-line,
.wbp-acc-buy {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px;
}
.wbp-sort {
	font-size: 13px;
	color: theme('colors.ihh-slate-gray');
}
.wbp-chip,
.wbp-pill,
.wbp-choose,
.wbp-select,
.wbp-add,
.wbp-liner-opt,
.wbp-stepper button {
	font-family: inherit;
	cursor: pointer;
}
.wbp-chip {
	min-height: 36px;
	padding: 0 16px;
	border-radius: 999px;
	font-size: 14px;
	font-weight: 500;
	border: 1.5px solid theme('colors.ihh-light-gray');
	background: theme('colors.ihh-white');
	color: theme('colors.ihh-midnight-gray');
}
.wbp-chip[aria-pressed='true'] {
	border-color: theme('colors.ihh-dark-salmon');
	background: theme('colors.ihh-salmon');
	color: theme('colors.ihh-midnight-gray');
}
.wbp-pill {
	min-height: 32px;
	padding: 0 12px;
	border-radius: 999px;
	font-size: 13px;
	font-weight: 500;
	border: 1.5px solid theme('colors.ihh-form-light-gray-hover');
	background: theme('colors.ihh-white');
	color: theme('colors.ihh-midnight-gray');
}
.wbp-pill.sm {
	min-height: 28px;
	font-size: 12px;
}
.wbp-pill[aria-pressed='true'] {
	border-color: theme('colors.ihh-dark-salmon');
	background: theme('colors.ihh-dark-salmon');
	color: theme('colors.ihh-midnight-gray');
}
.wbp-cards,
.wbp-liner-grid,
.wbp-acc-grid {
	display: grid;
	gap: 18px;
}
.wbp-cards {
	grid-template-columns: repeat(auto-fill, minmax(264px, 1fr));
}
.wbp-card,
.wbp-acc,
.wbp-summary,
.wbp-compare {
	background: theme('colors.ihh-white');
	border: 1px solid theme('colors.ihh-form-border');
	border-radius: 12px;
}
.wbp-card {
	display: flex;
	flex-direction: column;
	overflow: hidden;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}
.wbp-card.chosen {
	border: 2px solid theme('colors.ihh-dark-salmon');
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}
.wbp-card.oos {
	opacity: 0.62;
}
.wbp-card-photo {
	position: relative;
	background: theme('colors.ihh-pink');
	aspect-ratio: 4 / 3;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
}
.wbp-card-photo img,
.wbp-acc img {
	width: 100%;
	height: 100%;
	object-fit: contain;
	mix-blend-mode: multiply;
}
.wbp-badges {
	position: absolute;
	top: 10px;
	left: 10px;
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
}
.wbp-badge {
	font-size: 11px;
	font-weight: 600;
	padding: 4px 9px;
	border-radius: 999px;
}
.wbp-badge[data-tone='dark'] {
	background: theme('colors.ihh-midnight-gray');
	color: theme('colors.ihh-white');
}
.wbp-badge[data-tone='grey'] {
	background: theme('colors.ihh-light-gray');
	color: theme('colors.ihh-midnight-gray');
}
.wbp-badge[data-tone='green'] {
	background: theme('colors.ihh-light-purple');
	color: theme('colors.ihh-midnight-gray');
}
.wbp-badge[data-tone='coral'] {
	background: theme('colors.ihh-salmon');
	color: theme('colors.ihh-midnight-gray');
}
.wbp-card-body {
	padding: 16px 16px 18px;
	display: flex;
	flex-direction: column;
	gap: 12px;
	flex: 1;
}
.wbp-brand,
.wbp-acc-group h3 {
	font-size: 12px;
	font-weight: 600;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: theme('colors.ihh-midnight-gray');
}
.wbp-card h3,
.wbp-acc-name {
	margin: 0;
	font-size: 18px;
	font-weight: 600;
	line-height: 1.25;
}
.wbp-acc-name {
	font-size: 14px;
}
.wbp-price-row {
	display: flex;
	align-items: baseline;
	gap: 8px;
}
.wbp-price {
	font-size: 22px;
	font-weight: 600;
}
.wbp-specs {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 8px;
	background: theme('colors.ihh-pink');
	border-radius: 8px;
	padding: 10px;
}
.wbp-specs span,
.wbp-line-kind {
	display: block;
	font-size: 11px;
	color: theme('colors.ihh-slate-gray');
	text-transform: uppercase;
	letter-spacing: 0.06em;
}
.wbp-specs strong {
	font-size: 13px;
	font-weight: 500;
}
.wbp-opt-row > span:first-child {
	font-size: 12px;
	color: theme('colors.ihh-slate-gray');
	width: 42px;
}
.wbp-card ul {
	margin: 0;
	padding: 0 0 0 18px;
	display: flex;
	flex-direction: column;
	gap: 4px;
	font-size: 13px;
	line-height: 1.45;
	color: theme('colors.ihh-midnight-gray');
}
.wbp-card-action {
	margin-top: auto;
	display: flex;
	flex-direction: column;
	gap: 6px;
	text-align: center;
	font-size: 12px;
	color: theme('colors.ihh-slate-gray');
}
.wbp-choose,
.wbp-add {
	width: 100%;
	min-height: 44px;
	border-radius: 8px;
	font-size: 15px;
	font-weight: 600;
}
.wbp-choose {
	border: 1.5px solid theme('colors.ihh-midnight-gray');
	background: theme('colors.ihh-white');
	color: theme('colors.ihh-midnight-gray');
	transition: background 150ms, border-color 150ms, color 150ms;
}
.wbp-choose:hover:not(:disabled) {
	background: theme('colors.ihh-salmon');
	border-color: theme('colors.ihh-dark-salmon');
}
.wbp-choose.chosen {
	border-color: theme('colors.ihh-dark-salmon');
	background: theme('colors.ihh-dark-salmon');
	color: theme('colors.ihh-midnight-gray');
}
.wbp-choose.chosen:hover:not(:disabled) {
	background: theme('colors.ihh-salmon');
}
.wbp-choose:disabled,
.wbp-select:disabled {
	background: theme('colors.ihh-midnight-gray');
	color: theme('colors.ihh-white');
	border-color: theme('colors.ihh-midnight-gray');
	opacity: 0.4;
	cursor: not-allowed;
}
.wbp-liner-opt:disabled,
.wbp-stepper button:disabled {
	background: theme('colors.ihh-light-gray');
	color: theme('colors.ihh-slate-gray');
	border-color: theme('colors.ihh-form-border');
	cursor: not-allowed;
}
.wbp-swatch {
	width: 28px;
	height: 28px;
	border-radius: 999px;
	border: 2px solid theme('colors.ihh-white');
	box-shadow: 0 0 0 1px theme('colors.ihh-gray');
	padding: 0;
}
.wbp-swatch.on {
	box-shadow: 0 0 0 2px theme('colors.ihh-dark-salmon');
}
.wbp-compare {
	overflow: hidden;
	border-radius: 10px;
}
.wbp-compare-toggle {
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding: 16px 18px;
	background: theme('colors.ihh-pink');
	border: 0;
	font-size: 16px;
	font-weight: 600;
	color: theme('colors.ihh-midnight-gray');
	text-align: left;
	font-family: inherit;
	cursor: pointer;
}
.wbp-compare-toggle span:last-child {
	font-size: 13px;
	font-weight: 500;
	color: theme('colors.ihh-midnight-gray');
	text-decoration: underline;
	text-decoration-color: theme('colors.ihh-dark-salmon');
}
.wbp-scroll-hint {
	margin: 0;
	padding: 8px 14px 0;
	font-size: 12px;
	color: theme('colors.ihh-slate-gray');
}
.wbp-table-wrap {
	overflow-x: auto;
}
.wbp table {
	width: 100%;
	min-width: 860px;
	border-collapse: collapse;
	font-size: 13px;
}
.wbp th {
	text-align: left;
	color: theme('colors.ihh-slate-gray');
	font-size: 11px;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	font-weight: 600;
	padding: 12px 10px;
}
.wbp th:first-child,
.wbp td:first-child {
	padding-left: 14px;
}
.wbp td {
	padding: 11px 10px;
	border-top: 1px solid theme('colors.ihh-form-border');
	vertical-align: top;
}
.wbp tr.zebra {
	background: theme('colors.ihh-white');
}
.wbp tbody tr {
	background: theme('colors.ihh-form-background');
}
.wbp tbody tr.zebra {
	background: theme('colors.ihh-white');
}
.wbp tbody tr.chosen {
	background: theme('colors.ihh-salmon');
}
.wbp-row-name {
	font-weight: 600;
}
.wbp-nowrap {
	white-space: nowrap;
}
.wbp-select {
	min-height: 36px;
	padding: 0 14px;
	border-radius: 6px;
	font-size: 13px;
	font-weight: 600;
	white-space: nowrap;
	border: 1.5px solid theme('colors.ihh-midnight-gray');
	background: theme('colors.ihh-white');
	color: theme('colors.ihh-midnight-gray');
	transition: background 150ms, border-color 150ms;
}
.wbp-select:hover:not(:disabled) {
	background: theme('colors.ihh-salmon');
	border-color: theme('colors.ihh-dark-salmon');
}
.wbp-select.chosen {
	border-color: theme('colors.ihh-dark-salmon');
	background: theme('colors.ihh-dark-salmon');
	color: theme('colors.ihh-midnight-gray');
}
.wbp-dashed {
	border: 1.5px dashed theme('colors.ihh-gray');
	border-radius: 10px;
	padding: 28px;
	text-align: center;
	color: theme('colors.ihh-slate-gray');
	font-size: 15px;
}
.wbp-liner,
.wbp-acc-group {
	display: flex;
	flex-direction: column;
	gap: 12px;
}
.wbp-included,
.wbp-confirm {
	display: flex;
	align-items: center;
	gap: 12px;
	background: theme('colors.ihh-light-purple');
	border-radius: 10px;
	padding: 14px 16px;
	font-size: 14px;
	color: theme('colors.ihh-midnight-gray');
}
.wbp-liner-grid {
	grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
	gap: 12px;
}
.wbp-liner-opt {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 8px;
	text-align: left;
	padding: 16px;
	min-height: 150px;
	border-radius: 10px;
	color: theme('colors.ihh-midnight-gray');
	background: theme('colors.ihh-white');
	border: 1px solid theme('colors.ihh-light-gray');
}
.wbp-liner-opt.on {
	background: theme('colors.ihh-salmon');
	border: 2px solid theme('colors.ihh-dark-salmon');
}
.wbp-liner-title,
.wbp-liner-meta,
.wbp-subtotal,
.wbp-line {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	width: 100%;
}
.wbp-liner-title {
	font-size: 15px;
	font-weight: 600;
}
.wbp-liner-desc,
.wbp-acc-desc,
.wbp-fine {
	font-size: 13px;
	color: theme('colors.ihh-slate-gray');
	line-height: 1.45;
}
.wbp-liner-meta {
	margin-top: auto;
	align-items: baseline;
	font-size: 12px;
	color: theme('colors.ihh-slate-gray');
}
.wbp-liner-meta strong {
	font-size: 16px;
	color: theme('colors.ihh-midnight-gray');
}
.wbp-dot {
	width: 18px;
	height: 18px;
	border-radius: 999px;
	box-sizing: border-box;
	border: 2px solid theme('colors.ihh-gray');
	background: theme('colors.ihh-white');
	flex: 0 0 auto;
}
.wbp-dot.on {
	border: 6px solid theme('colors.ihh-dark-salmon');
}
.wbp-stepper {
	display: flex;
	align-items: center;
	border: 1px solid theme('colors.ihh-form-light-gray-hover');
	border-radius: 6px;
	background: theme('colors.ihh-white');
}
.wbp-stepper button {
	width: 44px;
	height: 44px;
	border: 0;
	background: transparent;
	font-size: 18px;
	color: theme('colors.ihh-midnight-gray');
}
.wbp-stepper.sm button {
	width: 36px;
	height: 36px;
	font-size: 17px;
}
.wbp-stepper span {
	min-width: 22px;
	text-align: center;
	font-weight: 600;
}
.wbp-acc-group h3 {
	margin: 0;
	font-size: 16px;
	letter-spacing: 0;
	text-transform: none;
	font-weight: 600;
}
.wbp-acc-grid {
	grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
	gap: 12px;
}
.wbp-acc {
	display: flex;
	gap: 12px;
	padding: 12px;
	border-radius: 10px;
}
.wbp-acc.on {
	border: 2px solid theme('colors.ihh-dark-salmon');
}
.wbp-acc img {
	width: 72px;
	height: 72px;
	border-radius: 6px;
	background: theme('colors.ihh-pink');
	flex: 0 0 auto;
}
.wbp-acc > div {
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 4px;
}
.wbp-acc-buy {
	justify-content: space-between;
	padding-top: 4px;
	font-weight: 600;
}
.wbp-summary {
	flex: 1 1 320px;
	min-width: 0;
	position: sticky;
	top: 7.5rem;
	align-self: flex-start;
	box-shadow: 0 6px 24px rgba(0, 0, 0, 0.07);
	overflow: hidden;
}
.wbp-summary-head {
	background: theme('colors.ihh-salmon');
	padding: 18px 20px;
}
.wbp-summary-head .wbp-kicker {
	color: theme('colors.ihh-midnight-gray');
}
.wbp-summary-title {
	font-size: 20px;
	font-weight: 600;
}
.wbp-summary-body,
.wbp-summary-foot {
	padding: 16px 20px;
	display: flex;
	flex-direction: column;
	gap: 14px;
}
.wbp-summary-foot {
	border-top: 1px solid theme('colors.ihh-form-border');
	padding-bottom: 20px;
}
.wbp-line {
	align-items: flex-start;
}
.wbp-line > div:first-child {
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 0;
}
.wbp-line-name {
	font-size: 14px;
	font-weight: 500;
	line-height: 1.3;
}
.wbp-line-side {
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: 2px;
}
.wbp-line-side button {
	border: 0;
	background: transparent;
	padding: 4px 0;
	font-size: 12px;
	color: theme('colors.ihh-midnight-gray');
	text-decoration: underline;
	text-decoration-color: theme('colors.ihh-dark-salmon');
	font-family: inherit;
	cursor: pointer;
}
.wbp-subtotal strong {
	font-size: 22px;
}
.wbp-warn {
	font-size: 13px;
	color: theme('colors.ihh-warning-text');
	background: theme('colors.ihh-warning');
	border-radius: 8px;
	padding: 10px 12px;
	line-height: 1.4;
}
.wbp-add {
	min-height: 52px;
	border: 0;
	font-size: 16px;
	font-weight: 600;
	letter-spacing: 0.02em;
	background: theme('colors.ihh-dark-salmon');
	color: theme('colors.ihh-midnight-gray');
	transition: background 150ms, transform 150ms;
}
.wbp-add:hover:not(:disabled) {
	background: theme('colors.ihh-salmon');
}
.wbp-add:active:not(:disabled) {
	transform: scale(0.95);
}
.wbp-add:disabled {
	background: theme('colors.ihh-midnight-gray');
	color: theme('colors.ihh-white');
	opacity: 0.4;
	cursor: not-allowed;
}
.wbp-confirm {
	border-radius: 8px;
	padding: 10px 12px;
	font-size: 13px;
}
.wbp-confirm a {
	color: theme('colors.ihh-midnight-gray');
	text-decoration: underline;
	text-decoration-color: theme('colors.ihh-dark-salmon');
	font-weight: 600;
}
.wbp-fine {
	font-size: 12px;
	color: theme('colors.ihh-slate-gray');
	margin: 0;
}
.wbp-sr {
	position: absolute;
	width: 1px;
	height: 1px;
	overflow: hidden;
	clip: rect(0 0 0 0);
}
.wbp-empty-filter {
	margin: 0;
	color: theme('colors.ihh-slate-gray');
}
button:focus-visible,
a:focus-visible {
	outline: 2px solid theme('colors.ihh-dark-salmon');
	outline-offset: 2px;
}
@media (max-width: 900px) {
	.wbp-hero {
		min-height: 320px;
	}
	.wbp-hero-copy {
		padding: 28px 20px 24px;
	}
	.wbp-hero h1 {
		font-size: 34px;
	}
	.wbp-summary {
		position: static;
		flex-basis: 100%;
	}
	.wbp-wrap {
		padding: 0 16px;
	}
}
@media (min-width: 768px) {
	.wbp {
		padding-top: 12px;
	}
}
@media (min-width: 960px) {
	.wbp-scroll-hint {
		display: none;
	}
}
</style>
